import { FC, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Link, useNavigate } from 'react-router-dom';
import { IoArrowBack } from 'react-icons/io5';
import { BsInputCursorText } from 'react-icons/bs';
import { FaRegFolderOpen } from 'react-icons/fa';
import { TbMessage2 } from 'react-icons/tb';
import { Button, Form, Tabs, Tag, Typography } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import Box from 'src/components/Box';
import Comment from 'src/components/Comment';
import FeedbackModal from 'src/components/FeedbackModal';
import { FormBuilderData } from 'src/types/formBuilder';
import {
  convertMinToHrsMin,
  convertMinToRoundedHours,
  getRequestStatusLabel,
  getStatusColor,
  parseDateToMMMDD,
} from 'src/utils/stringHelpers';
import asyncErrorHandler from 'src/utils/asyncErrorHandler';
import RequestForm from './RequestForm';
import RequestViewTabDeliverables from './RequestViewTabDeliverables';
import apiRequests from 'src/utils/api';
import apiRoutes from 'src/utils/apiRoutes';
import { Quill } from 'react-quill';
import { RequestFollowersGroup } from './RequestFollowersGroup';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store';
import useProjectAccess from 'src/hooks/useProjectAccess';

const { Text } = Typography;

interface RequestViewProps {
  request: Record<string, any>;
  formBuilder: FormBuilderData;
  resetFormTrigger?: number;
  onUpdate: (values: any) => Promise<Record<string, any>>;
}

const RequestView: FC<RequestViewProps> = ({
  request,
  formBuilder,
  resetFormTrigger = 0,
  onUpdate,
}) => {
  const user = useSelector(
    (globalState: RootState) => globalState.auth.user
  );

  const [form] = Form.useForm();
  const [tabKey, setTabKey] = useState('2');
  const [saving, setSaving] = useState(false);
  const [openFeedbackModal, setOpenFeedbackModal] = useState(false);
  const navigate = useNavigate();

  const onWrapUpdate = async (values: any) => {
    setSaving(true);

    try {
      await onUpdate(values);
    } catch (error) {
      asyncErrorHandler(error);
    } finally {
      setSaving(false);
    }
  };

  const createComment = async (values: any) => {
    const quill = new Quill(document.createElement('div'));
    quill.setText(values.approval_feedback);

    const delta = quill.getContents();
    const html = quill.root.innerHTML;

    const newComment = {
      text: html,
      text_delta: JSON.stringify(delta),
      type: 'comment',
    };

    await apiRequests.post(
      `${apiRoutes.PROJECT_REQUEST}/${request.uuid}/comments`,
      newComment
    );
  };

  const filterFollowers = (relatedUsers: any[]) =>
    (relatedUsers ?? []).filter(
      (u: any) => u.pivot?.type === 'follower' && u.type === 'contact'
    );

  const [followers, setFollowers] = useState<any[]>(() =>
    filterFollowers(request.related_users)
  );

  useEffect(() => {
    if (resetFormTrigger > 0) {
      form.resetFields();
    }
  }, [form, resetFormTrigger]);

  useEffect(() => {
    setFollowers(filterFollowers(request.related_users));
  }, [request.related_users]);

  const { canSeeRequestTime, canEditFollowers } = useProjectAccess();

  const showTimeSpent =
    canSeeRequestTime && request.time_spent && request.status === 'completed';

  return (
    <div>
      <FeedbackModal
        visible={openFeedbackModal}
        loading={saving}
        onClose={() => {
          setOpenFeedbackModal(false);
        }}
        onConfirm={async (values: any) => {
          values.approval_status = 'rejected';

          await createComment(values);
          await onWrapUpdate(values);

          setOpenFeedbackModal(false);
        }}
      />

      <div className="mb-2 flex items-center justify-between">
        <Button
          type="link"
          size="large"
          icon={<IoArrowBack />}
          style={{ paddingLeft: 0 }}
          onClick={() => {
            navigate('/dashboard');
          }}
        >
          Go back
        </Button>

        <Link
          to={`/requests/${request.uuid}/duplicate`}
          className="mr-1 text-[#0044CC]"
        >
          <Button>Duplicate</Button>
        </Link>
      </div>

      <Box>
        <Tabs
          activeKey={tabKey}
          size="small"
          className={`tab-fullscreen tab-secondary tab-compact${
            tabKey === '2' ? ' tab-no-border' : ''
          }`}
          items={[
            {
              key: '1',
              label: (
                <span className="flex items-center gap-2">
                  <BsInputCursorText /> Brief
                </span>
              ),
              children: (
                <RequestForm
                  form={form}
                  request={request}
                  formBuilder={formBuilder}
                  disableForm
                />
              ),
            },
            {
              key: '2',
              label: (
                <span className="flex items-center gap-2">
                  <TbMessage2 /> Messages
                </span>
              ),
              children: (
                <>
                  <div className="mb-5 rounded-md border border-gray-200 px-3 py-2">
                    <div className="flex items-center justify-between max-sm:hidden">
                      <Text type="secondary" strong>
                        PROJECT STARTED
                      </Text>

                      <Text type="secondary" className="!text-xs">
                        by {request.requester?.name ?? request.creator.name} at{' '}
                        {dayjs(request.created_at).format(
                          'MMM DD, YYYY - hh:mm a'
                        )}
                      </Text>
                    </div>

                    <div className="mb-0 font-bold sm:mb-3">
                      {request.subject}
                    </div>

                    <div className={`grid ${canSeeRequestTime ? 'grid-cols-3' : 'grid-cols-2'} sm:grid-cols-2 gap-4 sm:gap-2 items-center ${
                        showTimeSpent
                          ? 'lg:grid-cols-5'
                          : canSeeRequestTime
                          ? 'md:grid-cols-4'
                          : 'md:grid-cols-3'
                      }`}
                    >
                      {canSeeRequestTime && (
                        <div>
                          <div className="max-sm:hidden">
                            <Text type="secondary">Hours estimated</Text>
                          </div>

                          <div className="text-xl max-sm:hidden">
                            {request.estimated_min || request.estimated_max
                              ? `${convertMinToHrsMin(
                                  request.estimated_min
                                )} - ${convertMinToHrsMin(
                                  request.estimated_max
                                )} h`
                              : '-'}
                          </div>

                          <div className="sm:hidden">
                            <Text className="!text-xs" type="secondary" ellipsis>
                              {request.estimated_min || request.estimated_max
                                ? `${convertMinToRoundedHours(
                                    request.estimated_min
                                  )} - ${convertMinToRoundedHours(
                                    request.estimated_max
                                  )} h`
                                : 'No estimated time'}
                            </Text>
                          </div>
                        </div>
                      )}

                      {showTimeSpent ? (
                        <div className="flex-1 basis-1/2 sm:flex-none">
                          <div className="max-sm:hidden">
                            <Text type="secondary">Total time spent</Text>
                          </div>  

                          <div className="text-xl max-sm:hidden">
                            {convertMinToHrsMin(request.time_spent)} h
                          </div>

                          <div className="sm:hidden">
                            <Text className="!text-xs" type="secondary" ellipsis>
                              {convertMinToHrsMin(request.time_spent)} h
                            </Text>
                          </div>
                        </div>
                      ) : undefined}

                      <div>
                        <div className="max-sm:hidden">
                          <Text type="secondary">Delivery date</Text>
                        </div>

                        <div className="text-xl max-sm:hidden">
                          {request.target_date
                            ? parseDateToMMMDD(request.target_date, true)
                            : '-'}
                        </div>

                        <div className="sm:hidden">
                          <Text className="!text-xs" type="secondary" ellipsis>
                            {`${parseDateToMMMDD(request.target_date, true)}`}
                          </Text>
                        </div>
                      </div>

                      <div>
                        <div className="max-sm:hidden">
                          <Text type="secondary">Request status</Text>
                        </div>                 

                        <Tag
                          className="!text-center max-sm:w-full"
                          color={getStatusColor(request.status)}
                        >
                          {getRequestStatusLabel(request.status)}
                        </Tag>
                      </div>

                      <div className="max-sm:col-span-2">
                        <div className="max-sm:hidden">
                          <Text type="secondary">Followers</Text>
                        </div>    

                        <RequestFollowersGroup
                          editable={canEditFollowers}
                          followers={followers}
                          setFollowers={setFollowers}
                          requestUuid={request.uuid}
                        />
                      </div>

                      {request.status === 'waiting_approval' && user?.contact?.type === 'contact' ? (
                        <div className="max-sm:col-span-full w-full sm:hidden grid grid-cols-2 gap-2">
                          <Button
                            className="btn-green-outline flex-auto"
                            size="large"
                            icon={<CheckOutlined className="text-base" />}
                            loading={saving}
                            onClick={() =>
                              onWrapUpdate({ approval_status: 'approved' })
                            }
                          >
                            Approve
                          </Button>

                          <Button
                            className="btn-red-outline flex-auto"
                            size="large"
                            icon={<CloseOutlined className="text-base" />}
                            loading={saving}
                            onClick={() => setOpenFeedbackModal(true)}
                          >
                            Reject
                          </Button>
                        </div>
                      ) : undefined}
                    </div>
                  </div>

                  {request.status === 'waiting_approval' && user?.contact?.type === 'contact' ? (
                    <div className="flex gap-4 max-sm:hidden">
                      <Button
                        className="btn-green-outline flex-auto"
                        size="large"
                        icon={<CheckOutlined className="text-base" />}
                        loading={saving}
                        onClick={() =>
                          onWrapUpdate({ approval_status: 'approved' })
                        }
                      >
                        Approve
                      </Button>

                      <Button
                        className="btn-red-outline flex-auto"
                        size="large"
                        icon={<CloseOutlined className="text-base" />}
                        loading={saving}
                        onClick={() => setOpenFeedbackModal(true)}
                      >
                        Reject
                      </Button>
                    </div>
                  ) : undefined}

                  <div>
                    <Comment
                      type="requests"
                      typeUuid={request.uuid}
                      showSendMessage={
                        request.status !== 'completed' &&
                        request.status !== 'cancelled'
                      }
                      scrollClassName="max-h-[calc(80vh-200px)] mt-4"
                      projectUuid={request.project?.uuid ?? ''}
                      mentionTypes={['regular_user', 'contact']}
                    />
                  </div>
                </>
              ),
            },
            {
              key: '3',
              label: (
                <span className="flex items-center gap-2">
                  <FaRegFolderOpen /> Deliverables
                </span>
              ),
              children: (
                <RequestViewTabDeliverables
                  request={request}
                  tabKey={tabKey}
                  onChangeTab={setTabKey}
                />
              ),
            },
          ]}
          onChange={setTabKey}
        />
      </Box>
    </div>
  );
};

export default RequestView;
